import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Pencil, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { getClients, createClient, updateClient, deleteClient } from '@/lib/api';
import { toast } from 'sonner';

const INDUSTRIES = [
  { value: 'fintech', label: 'Fintech / SaaS' },
  { value: 'health', label: 'Health & Fitness' },
  { value: 'gaming', label: 'Gaming' },
  { value: 'ecommerce', label: 'E-Commerce' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Aktiv' },
  { value: 'paused', label: 'Pausiert' },
  { value: 'churned', label: 'Abgewandert' },
];

const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

export default function AdminClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form, setForm] = useState({ name: '', industry: 'fintech', contact_email: '', contact_person: '', app_name: '', status: 'active', notes: '' });

  useEffect(() => { loadClients(); }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const res = await getClients();
      setClients(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (editingClient) {
        await updateClient(editingClient.id, form);
        toast.success('Kunde aktualisiert');
      } else {
        await createClient(form);
        toast.success('Kunde erstellt');
      }
      setDialogOpen(false);
      setEditingClient(null);
      setForm({ name: '', industry: 'fintech', contact_email: '', contact_person: '', app_name: '', status: 'active', notes: '' });
      loadClients();
    } catch (err) { toast.error('Fehler beim Speichern'); }
  };

  const handleEdit = (cl) => {
    setEditingClient(cl);
    setForm({ name: cl.name, industry: cl.industry, contact_email: cl.contact_email, contact_person: cl.contact_person, app_name: cl.app_name, status: cl.status, notes: cl.notes || '' });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Kunden wirklich löschen?')) return;
    try {
      await deleteClient(id);
      toast.success('Kunde gelöscht');
      loadClients();
    } catch (err) { toast.error('Fehler beim Löschen'); }
  };

  const filtered = clients.filter(cl => {
    const matchSearch = cl.name?.toLowerCase().includes(search.toLowerCase()) || cl.app_name?.toLowerCase().includes(search.toLowerCase());
    const matchIndustry = filterIndustry === 'all' || cl.industry === filterIndustry;
    return matchSearch && matchIndustry;
  });

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <PageHeader title="Kunden (CRM)" description={`${clients.length} Kunden verwaltet`}>
        <Button onClick={() => { setEditingClient(null); setForm({ name: '', industry: 'fintech', contact_email: '', contact_person: '', app_name: '', status: 'active', notes: '' }); setDialogOpen(true); }} className="btn-lift bg-slate-900 text-white hover:bg-slate-800" data-testid="admin-client-create-dialog-open-button">
          <Plus className="w-4 h-4 mr-2" /> Neuer Kunde
        </Button>
      </PageHeader>

      <motion.div variants={fadeIn} className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Kunden suchen..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="clients-search-input" />
        </div>
        <Select value={filterIndustry} onValueChange={setFilterIndustry}>
          <SelectTrigger className="w-full sm:w-44" data-testid="clients-filter-industry">
            <SelectValue placeholder="Branche" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Branchen</SelectItem>
            {INDUSTRIES.map(ind => <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </motion.div>

      {filtered.length === 0 && !loading ? (
        <EmptyState title="Keine Kunden" description="Erstellen Sie Ihren ersten Kunden, um loszulegen." actionLabel="Kunde erstellen" action={() => setDialogOpen(true)} />
      ) : (
        <motion.div variants={fadeIn} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="clients-table">
              <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="text-left p-3 pl-4 font-medium text-slate-500">Name</th>
                <th className="text-left p-3 font-medium text-slate-500">App</th>
                <th className="text-left p-3 font-medium text-slate-500">Branche</th>
                <th className="text-left p-3 font-medium text-slate-500">Kontakt</th>
                <th className="text-left p-3 font-medium text-slate-500">Status</th>
                <th className="text-right p-3 pr-4 font-medium text-slate-500">Aktionen</th>
              </tr></thead>
              <tbody>
                {filtered.map((cl, i) => (
                  <tr key={cl.id || i} className="border-b border-slate-50 last:border-0 hover:bg-slate-50" data-testid="clients-table-row">
                    <td className="p-3 pl-4 font-medium text-slate-900">{cl.name}</td>
                    <td className="p-3 text-slate-700">{cl.app_name}</td>
                    <td className="p-3"><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{INDUSTRIES.find(i => i.value === cl.industry)?.label || cl.industry}</span></td>
                    <td className="p-3 text-slate-700">{cl.contact_person}<br/><span className="text-xs text-slate-400">{cl.contact_email}</span></td>
                    <td className="p-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cl.status === 'active' ? 'bg-emerald-50 text-emerald-700' : cl.status === 'paused' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>{STATUS_OPTIONS.find(s => s.value === cl.status)?.label || cl.status}</span></td>
                    <td className="p-3 pr-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(cl)} data-testid={`client-edit-${cl.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(cl.id)} className="text-red-500 hover:text-red-700" data-testid={`client-delete-${cl.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editingClient ? 'Kunde bearbeiten' : 'Neuer Kunde'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Name *</label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Firmenname" data-testid="client-form-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Branche</label>
                <Select value={form.industry} onValueChange={v => setForm({...form, industry: v})}><SelectTrigger data-testid="client-form-industry"><SelectValue /></SelectTrigger><SelectContent>{INDUSTRIES.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Status</label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}><SelectTrigger data-testid="client-form-status"><SelectValue /></SelectTrigger><SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">App Name</label><Input value={form.app_name} onChange={e => setForm({...form, app_name: e.target.value})} placeholder="App Name" data-testid="client-form-app" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Kontaktperson</label><Input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} placeholder="Vorname Nachname" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">E-Mail</label><Input value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} placeholder="email@beispiel.de" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} className="btn-lift bg-slate-900 text-white hover:bg-slate-800" data-testid="client-form-save">{editingClient ? 'Aktualisieren' : 'Erstellen'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
